// 自定义指令：自动计算和设置 overflowWidth CSS 变量
// 用于文本滚动动画，计算文本超出容器部分的宽度

interface OverflowWidthBinding {
  value?: {
    // 可选配置项
    debounce?: number; // 防抖延迟时间，默认 100ms
    resize?: boolean; // 是否监听窗口大小变化，默认 true
  };
}

type OverflowWidthElement = HTMLElement & {
  _overflowWidthHandler: (() => void) | null;
  _resizeHandler: (() => void) | null;
  _resizeObserver: ResizeObserver | null;
};

function calculateOverflowWidth(el: OverflowWidthElement): number {
  const container = el;
  const textElement = container.querySelector('div') || container;
  
  if (!textElement) return 0;
  
  const containerWidth = container.clientWidth;
  const textWidth = textElement.scrollWidth;
  
  // 计算超出部分的宽度
  const overflowWidth = Math.max(0, textWidth - containerWidth);
  
  return overflowWidth;
}

function setOverflowWidth(el: OverflowWidthElement, binding: OverflowWidthBinding) {
  const overflowWidth = calculateOverflowWidth(el);
  el.style.setProperty('--overflow-width', `${overflowWidth}px`);
}

// 防抖函数
function debounce<T extends(...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: any;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default {
  mounted(el: OverflowWidthElement, binding: OverflowWidthBinding) {
    const config = binding.value || {};
    const debounceTime = config.debounce || 100;
    const shouldResize = config.resize !== false;
    
    // 创建防抖版本的设置函数
    const debouncedSetOverflowWidth = debounce(() => {
      setOverflowWidth(el, binding);
    }, debounceTime);
    
    // 存储防抖函数引用，以便在 unmounted 时清理
    el._overflowWidthHandler = debouncedSetOverflowWidth;
    
    // 初始设置
    setOverflowWidth(el, binding);
    
    // 监听窗口大小变化
    if (shouldResize) {
      window.addEventListener('resize', debouncedSetOverflowWidth);
      el._resizeHandler = debouncedSetOverflowWidth;
    }
    
    // 监听元素大小变化（使用 ResizeObserver）
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        debouncedSetOverflowWidth();
      });
      resizeObserver.observe(el);
      el._resizeObserver = resizeObserver;
    }
  },
  
  updated(el: OverflowWidthElement, binding: OverflowWidthBinding) {
    // 当指令值更新时，重新计算
    setOverflowWidth(el, binding);
  },
  
  unmounted(el: OverflowWidthElement) {
    // 清理事件监听器
    if (el._resizeHandler) {
      window.removeEventListener('resize', el._resizeHandler);
    }
    
    if (el._resizeObserver) {
      el._resizeObserver.disconnect();
    }
    
    // 清理引用
    el._overflowWidthHandler = null;
    el._resizeHandler = null;
    el._resizeObserver = null;
  }
};
